import React from "react";
import { Loader } from "@design-system/Loader/Loader";
import styles from "./LoaderOnInitialLoading.module.scss";

export interface LoaderOnInitialLoadingQueryResult<TData> {
    isLoading: boolean;
    isSuccess: boolean;
    isPlaceholderData?: boolean;
    data?: TData;
}

interface Props<TData> {
    queryResult: LoaderOnInitialLoadingQueryResult<TData>;
    children: React.ReactNode;
    // When the query keeps previous data across a refetch (e.g.
    // `placeholderData: keepPreviousData`), dim the stale content and show a
    // loader so users don't mistake the previous result for the new selection.
    dimWhileFetching?: boolean;
}

export const LoaderOnInitialLoading = <TData,>({
    queryResult,
    children,
    dimWhileFetching = false,
}: Props<TData>): React.ReactElement => {
    const showContent = queryResult.isSuccess && !!queryResult.data;
    // Dim only while showing kept-previous (placeholder) data — i.e. while a
    // new/disabled selection's result isn't ready yet, including the "withhold"
    // window. `isPlaceholderData` (not `isFetching`) is the right signal: a
    // same-key background refetch keeps the current data, so it must not dim.
    const isShowingStaleData =
        dimWhileFetching && showContent && queryResult.isPlaceholderData;

    return (
        <>
            {queryResult.isLoading && <Loader />}
            {showContent &&
                (dimWhileFetching ? (
                    <div className={styles.wrapper}>
                        {isShowingStaleData && (
                            <div className={styles.overlay}>
                                <Loader />
                            </div>
                        )}
                        <div
                            className={
                                isShowingStaleData ? styles.dimmed : undefined
                            }
                        >
                            {children}
                        </div>
                    </div>
                ) : (
                    children
                ))}
        </>
    );
};
